import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Message } from '@/services/chat/chatService';
import { TickerTagPicker } from './TickerTagPicker';
import { MentionAutocomplete } from './MentionAutocomplete';
import {
  detectMentions,
  parseMentionTrigger,
  getMentionSuggestions,
  insertMention,
} from '@/services/chat/mentionService';
import { GroupMember } from '@/services/groups/groupService';
import { TickerAutocomplete } from './TickerAutocomplete';
import {
  parseTickerTrigger,
  getTickerSuggestions,
  insertTicker,
  extractTickersFromMessage,
  TickerSuggestion,
} from '@/services/chat/tickerAutocompleteService';

interface MessageInputProps {
  onSend: (
    message: string,
    manualTickers?: string[],
    replyToMessageId?: string,
    mentionedUsers?: Array<{ user_id: string; name: string; position: number }>
  ) => void;
  disabled?: boolean;
  sending?: boolean;
  replyToMessage?: Message | null;
  onCancelReply?: () => void;
  suggestedTickers?: string[];
  recentTickers?: string[];
  groupMembers?: GroupMember[];
  currentUserId?: string;
}

const MAX_LENGTH = 1000;
const SHOW_COUNTER_AT = 900;

export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  disabled = false,
  sending = false,
  replyToMessage = null,
  onCancelReply,
  suggestedTickers = [],
  recentTickers = [],
  groupMembers = [],
  currentUserId,
}) => {
  const [message, setMessage] = useState('');
  const [manualTickers, setManualTickers] = useState<string[]>([]);
  const [showTickerPicker, setShowTickerPicker] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState<GroupMember[]>([]);
  const [showTickerAutocomplete, setShowTickerAutocomplete] = useState(false);
  const [tickerSuggestions, setTickerSuggestions] = useState<TickerSuggestion[]>([]);
  const inputRef = useRef<TextInput>(null);
  const insets = useSafeAreaInsets();

  // Debug: Log group members when they change
  React.useEffect(() => {
    console.log(`👥 [MessageInput] Group members updated: ${groupMembers.length} members`);
    if (groupMembers.length > 0) {
      console.log(`👥 [MessageInput] Members:`, groupMembers.map(m => m.user.name).join(', '));
    }
  }, [groupMembers]);

  const handleTextChange = async (text: string) => {
    setMessage(text);

    // Use text length as cursor position since it's typically at the end when typing
    const assumedCursorPos = text.length;

    // Check for ticker trigger ($) first
    const tickerInfo = parseTickerTrigger(text, assumedCursorPos);
    if (tickerInfo && tickerInfo.isTypingTicker) {
      console.log(`💰 [MessageInput] Ticker detected! Partial: "${tickerInfo.partial}"`);
      // Show ticker autocomplete
      const suggestions = await getTickerSuggestions(tickerInfo.partial);
      console.log(`📊 [MessageInput] Found ${suggestions.length} ticker suggestions`);
      setTickerSuggestions(suggestions);
      setShowTickerAutocomplete(true);
      // Hide mention autocomplete when showing ticker
      setShowMentionAutocomplete(false);
      return;
    }

    // Hide ticker autocomplete if no trigger
    if (showTickerAutocomplete) {
      setShowTickerAutocomplete(false);
    }

    // Check if user is typing a mention (only if no ticker)
    if (groupMembers.length > 0) {
      const mentionInfo = parseMentionTrigger(text, assumedCursorPos);

      if (mentionInfo && mentionInfo.isMentioning) {
        console.log(`🔔 [MessageInput] Mention detected! Partial: "${mentionInfo.partial}"`);
        // Show autocomplete with filtered suggestions
        const suggestions = getMentionSuggestions(mentionInfo.partial, groupMembers);
        console.log(
          `👥 [MessageInput] Found ${suggestions.length} matching members out of ${groupMembers.length} total`
        );
        setMentionSuggestions(suggestions);
        setShowMentionAutocomplete(true);
      } else {
        // Hide autocomplete
        setShowMentionAutocomplete(false);
        setMentionSuggestions([]);
      }
    }
  };

  const handleSelectionChange = (event: any) => {
    const position = event.nativeEvent.selection.start;
    setCursorPosition(position);

    // Re-check mention status at new cursor position
    const mentionInfo = parseMentionTrigger(message, position);
    if (mentionInfo && mentionInfo.isMentioning) {
      const suggestions = getMentionSuggestions(mentionInfo.partial, groupMembers);
      setMentionSuggestions(suggestions);
      setShowMentionAutocomplete(true);
    } else {
      setShowMentionAutocomplete(false);
      setMentionSuggestions([]);
    }
  };

  const handleMentionSelect = (member: GroupMember) => {
    // Find the mention trigger position
    const mentionInfo = parseMentionTrigger(message, cursorPosition);

    if (mentionInfo) {
      // Insert the mention at the correct position
      const { newText, newCursorPosition } = insertMention(
        message,
        mentionInfo.startPos,
        cursorPosition,
        member
      );

      setMessage(newText);
      setShowMentionAutocomplete(false);
      setMentionSuggestions([]);

      // Set cursor position after the mention
      setTimeout(() => {
        inputRef.current?.setNativeProps({
          selection: { start: newCursorPosition, end: newCursorPosition },
        });
      }, 10);
    }
  };

  const handleTickerSelect = (ticker: string) => {
    console.log(`💰 [MessageInput] Ticker selected: ${ticker}`);
    // Find the ticker trigger position
    const tickerInfo = parseTickerTrigger(message, cursorPosition);

    if (tickerInfo) {
      // Insert the ticker at the correct position
      const { newText, newCursorPosition } = insertTicker(
        message,
        tickerInfo.startPos,
        cursorPosition,
        ticker
      );

      setMessage(newText);
      setShowTickerAutocomplete(false);
      setTickerSuggestions([]);

      // Also add to manual tickers if not already there
      if (!manualTickers.includes(ticker)) {
        handleAddTicker(ticker);
      }

      // Set cursor position after the ticker
      setTimeout(() => {
        inputRef.current?.setNativeProps({
          selection: { start: newCursorPosition, end: newCursorPosition },
        });
      }, 10);
    }
  };

  const handleSend = () => {
    const trimmed = message.trim();
    if (trimmed.length > 0 && !sending) {
      // Detect mentions in message
      const mentions = detectMentions(trimmed, groupMembers);

      // Extract $ tickers from message text
      const autocompleteTickers = extractTickersFromMessage(trimmed);

      // Merge manual tickers and autocomplete tickers (deduplicate)
      const allTickers = [...new Set([...manualTickers, ...autocompleteTickers])];

      console.log(`📊 [MessageInput] Sending with ${allTickers.length} tickers:`, allTickers);

      onSend(
        trimmed,
        allTickers.length > 0 ? allTickers : undefined,
        replyToMessage?.id,
        mentions.length > 0 ? mentions : undefined
      );
      setMessage('');
      setManualTickers([]);
      setShowMentionAutocomplete(false);
      setMentionSuggestions([]);
      setShowTickerAutocomplete(false);
      setTickerSuggestions([]);
      if (onCancelReply) {
        onCancelReply();
      }
    }
  };

  const handleAddTicker = (ticker: string) => {
    if (!manualTickers.includes(ticker)) {
      setManualTickers([...manualTickers, ticker]);
    }
  };

  const handleRemoveTicker = (ticker: string) => {
    setManualTickers(manualTickers.filter(t => t !== ticker));
  };

  const handleAddSuggested = (ticker: string) => {
    handleAddTicker(ticker);
  };

  const showCounter = message.length >= SHOW_COUNTER_AT;
  const remaining = MAX_LENGTH - message.length;

  // Filter out already manually tagged tickers from suggestions
  const filteredSuggestions = suggestedTickers.filter(ticker => !manualTickers.includes(ticker));

  return (
    <>
      {/* Mention Autocomplete */}
      <MentionAutocomplete
        visible={showMentionAutocomplete}
        suggestions={mentionSuggestions}
        onSelect={handleMentionSelect}
        currentUserId={currentUserId}
      />

      {/* Ticker Autocomplete */}
      <TickerAutocomplete
        visible={showTickerAutocomplete}
        suggestions={tickerSuggestions}
        onSelect={handleTickerSelect}
      />

      <View style={[styles.container, { paddingBottom: Math.max(insets.bottom, 12) }]}>
        {/* Reply Indicator (WhatsApp/Telegram Style) */}
        {replyToMessage && (
          <View style={styles.replyContainer}>
            <View style={styles.replyBar} />

            <View style={styles.replyContent}>
              {/* Header: Icon + User Name */}
              <View style={styles.replyHeader}>
                <Ionicons name="arrow-undo" size={14} color={Colors.accent.primary} />
                <Text style={styles.replyUserName}>{replyToMessage.user?.name}</Text>
              </View>

              {/* Message Preview */}
              <Text style={styles.replyMessagePreview} numberOfLines={2}>
                {replyToMessage.content}
              </Text>

              {/* Show ticker context being inherited */}
              {(replyToMessage.metadata?.extracted_tickers ||
                replyToMessage.metadata?.manual_tickers) && (
                <View style={styles.replyInheritedContext}>
                  <Ionicons name="trending-up" size={10} color={Colors.ai.primary} />
                  <Text style={styles.replyContextText}>
                    Replying about:{' '}
                    {[
                      ...(replyToMessage.metadata.extracted_tickers || []),
                      ...(replyToMessage.metadata.manual_tickers || []),
                    ]
                      .map(t => `$${t}`)
                      .join(', ')}
                  </Text>
                </View>
              )}
            </View>

            <TouchableOpacity onPress={onCancelReply} style={styles.cancelReplyButton}>
              <Ionicons name="close" size={18} color={Colors.text.tertiary} />
            </TouchableOpacity>
          </View>
        )}

        {/* Manual Ticker Tags */}
        {manualTickers.length > 0 && (
          <View style={styles.tickersContainer}>
            <Text style={styles.tickersLabel}>Tickers:</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.tickersScroll}
            >
              {manualTickers.map(ticker => (
                <View key={ticker} style={styles.tickerChip}>
                  <Text style={styles.tickerChipText}>${ticker}</Text>
                  <TouchableOpacity
                    onPress={() => handleRemoveTicker(ticker)}
                    hitSlop={{ top: 5, bottom: 5, left: 5, right: 5 }}
                  >
                    <Ionicons name="close-circle" size={16} color={Colors.text.primary} />
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>
          </View>
        )}

        {/* AI Suggested Tickers */}
        {filteredSuggestions.length > 0 && (
          <View style={styles.suggestionsContainer}>
            <Text style={styles.suggestionsLabel}>Suggested:</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.suggestionsScroll}
            >
              {filteredSuggestions.map(ticker => (
                <TouchableOpacity
                  key={ticker}
                  style={styles.suggestionChip}
                  onPress={() => handleAddSuggested(ticker)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.suggestionChipText}>${ticker}</Text>
                  <Ionicons name="add-circle" size={14} color={Colors.ai.primary} />
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {showCounter && (
          <View style={styles.counterContainer}>
            <Text style={[styles.counterText, remaining < 50 && styles.counterWarning]}>
              {remaining} characters remaining
            </Text>
          </View>
        )}

        <View style={styles.inputContainer}>
          <TouchableOpacity
            style={styles.tagButton}
            onPress={() => setShowTickerPicker(true)}
            disabled={disabled || sending}
            activeOpacity={0.7}
          >
            <Ionicons
              name="pricetag"
              size={20}
              color={disabled || sending ? Colors.text.tertiary : Colors.accent.primary}
            />
          </TouchableOpacity>

          <TextInput
            ref={inputRef}
            style={styles.input}
            placeholder={sending ? 'Sending...' : 'Type a message...'}
            placeholderTextColor={Colors.text.tertiary}
            value={message}
            onChangeText={handleTextChange}
            onSelectionChange={handleSelectionChange}
            multiline
            maxLength={MAX_LENGTH}
            editable={!disabled && !sending}
          />

          <TouchableOpacity
            style={[
              styles.sendButton,
              (!message.trim() || disabled || sending) && styles.sendButtonDisabled,
            ]}
            onPress={handleSend}
            disabled={!message.trim() || disabled || sending}
            activeOpacity={0.7}
          >
            <Ionicons
              name={sending ? 'time-outline' : 'send'}
              size={20}
              color={
                message.trim() && !disabled && !sending ? Colors.text.primary : Colors.text.tertiary
              }
            />
          </TouchableOpacity>
        </View>
      </View>

      {/* Ticker Tag Picker Modal */}
      <TickerTagPicker
        visible={showTickerPicker}
        onClose={() => setShowTickerPicker(false)}
        onSelect={handleAddTicker}
        selectedTickers={manualTickers}
        recentTickers={recentTickers}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
    backgroundColor: Colors.background.secondary,
    paddingHorizontal: 20,
    paddingTop: 16,
    // paddingBottom is set dynamically with safe area insets (minimum 12px)
  },
  counterContainer: {
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 8,
  },
  counterText: {
    fontSize: 11,
    color: Colors.text.tertiary,
    textAlign: 'right',
  },
  counterWarning: {
    color: Colors.status.warning,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  input: {
    flex: 1,
    backgroundColor: Colors.background.tertiary,
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    paddingTop: 10,
    fontSize: 15,
    color: Colors.text.primary,
    maxHeight: 100,
    marginRight: 8,
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Colors.accent.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    backgroundColor: Colors.background.tertiary,
  },
  // WhatsApp/Telegram-style reply indicator
  replyContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: `${Colors.accent.primary}10`,
    padding: 12,
    marginBottom: 12,
    borderRadius: 8,
    gap: 12,
  },
  replyBar: {
    width: 4,
    alignSelf: 'stretch',
    borderRadius: 2,
    backgroundColor: Colors.accent.primary,
  },
  replyContent: {
    flex: 1,
    gap: 4,
  },
  replyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  replyUserName: {
    fontSize: 13,
    fontWeight: '700',
    color: Colors.accent.primary,
  },
  replyMessagePreview: {
    fontSize: 12,
    color: Colors.text.secondary,
    lineHeight: 16,
  },
  replyInheritedContext: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  replyContextText: {
    fontSize: 10,
    color: Colors.ai.primary,
    fontStyle: 'italic',
  },
  cancelReplyButton: {
    padding: 4,
  },
  tickersContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  tickersLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.secondary,
    marginRight: 8,
  },
  tickersScroll: {
    flexDirection: 'row',
    gap: 6,
  },
  tickerChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.accent.primary,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  tickerChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  suggestionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 6,
    backgroundColor: `${Colors.ai.primary}08`,
  },
  suggestionsLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.ai.primary,
    marginRight: 8,
  },
  suggestionsScroll: {
    flexDirection: 'row',
    gap: 6,
  },
  suggestionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderWidth: 1,
    borderColor: Colors.ai.primary,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    gap: 3,
  },
  suggestionChipText: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.ai.primary,
  },
  tagButton: {
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
});
